/**
 * useCreateVersion — State machine driving the Create New Version flow.
 *
 * The UI is a single continuous chat view. Phases control which extra
 * elements render inline (approach cards, thinking, review artifacts, etc.)
 * but the chat + input bar remain visible throughout.
 *
 * Phases:
 *  1. approach   – greeting + approach cards shown inline, input bar visible
 *  2. chat       – conversational planner (draft endpoint loops until ready)
 *  3. planning   – "Thinking..." animation shown inline
 *  4. running    – workflow started, progress shown inline
 *  5. review     – review_artifacts available (insights + changes)
 *  6. saving     – apply endpoint called
 *  7. done       – version created confirmation
 *  8. error      – something went wrong
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useOrganization } from '@/hooks/useOrganization';
import versionWorkflowService, {
  type ApplyAction,
  type DraftRequest,
  type DraftResponse,
  type PlannerMessage,
  type DraftPlan,
  type PromptChange,
  type WorkflowStatus,
  type ReviewArtifacts,
  type ABTestSummary,
  type KnowledgeGapStatus,
} from '@/lib/api/versionWorkflow';
import performanceService, {
  type RubricOverviewItem,
} from '@/lib/api/performance';

export type Phase =
  | 'approach'
  | 'chat'
  | 'confirm'
  | 'confirm_simulation'
  | 'planning'
  | 'running'
  | 'review'
  | 'scores_ready'
  | 'saving'
  | 'ab_running'
  | 'ab_results'
  | 'done'
  | 'error';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  /** Optional override sent to planner instead of content */
  plannerContent?: string;
  /** Timestamp for display ordering */
  ts: number;
}

export interface CreateVersionState {
  phase: Phase;
  intent: 'score_driven' | 'targeted_revision' | null;
  messages: ChatMessage[];
  plannerState: Record<string, unknown> | null;
  draftPlan: DraftPlan | null;
  batchId: string | null;
  workflowStatus: WorkflowStatus | null;
  reviewArtifacts: ReviewArtifacts | null;
  abTestSummary: ABTestSummary | null;
  newVersionNumber: number | null;
  error: string | null;
  isSending: boolean;
  rubricNames: string[];
  /** Full rubric data from eval overview — used for the inline scores card */
  evalRubrics: RubricOverviewItem[];
  /** Label for the eval set (e.g. "Custom Eval Scores") */
  evalSetName: string | null;
  /** Whether eval insights are currently being fetched */
  insightsLoading: boolean;
  /** LLM-generated suggestion labels for pills (score_driven flow) */
  suggestionLabels: string[];
  /** Workflow request stashed during the confirm_simulation phase — populated
   *  when the user clicks "Simulate & Score" and consumed when they pick a
   *  run mode (sims only / sims + KG) on the confirm card. */
  pendingSimulationRequest: Record<string, unknown> | null;
}

/** Workflow info surfaced when an abandoned workflow is detected on mount. */
export interface ActiveWorkflowInfo {
  batchId: string;
  status: string;
  startedAt: string | null;
}

/**
 * Statuses that indicate a workflow has finished and cannot be resumed.
 * Mirrors the backend's _TERMINAL_STATUSES in optimization_workflow_service.py.
 */
const TERMINAL_STATUSES = new Set([
  'workflow_complete',
  'workflow_complete_no_changes',
  'workflow_complete_regression',
  'workflow_complete_inconclusive',
  'completed',
  'failed',
  'cancelled',
]);

/**
 * Single source of truth for "is the parallel knowledge-gap analysis still
 * progressing?" Both `'queued'` (run row not yet created — workflow is still
 * pre-scoring) and `'running'` (KG actively executing) are in-flight states
 * that should keep the polling loop alive so the KG status card can refresh.
 * Used by the polling loop AND by `resumeWorkflow` so the two stay in sync.
 */
const isKgInFlight = (
  status?: KnowledgeGapStatus | null,
): boolean => status === 'running' || status === 'queued';

let _msgId = 0;
function nextMsgId(): string {
  return `msg-${++_msgId}-${Date.now()}`;
}

function buildWorkflowStatusSnapshot(
  organizationId: string,
  batchId: string,
  status: string,
): WorkflowStatus {
  return {
    batch_id: batchId,
    organization_id: organizationId,
    status,
    dataset_names: [],
    optimization_run_id: null,
    prompt_name: null,
    new_prompt_version: null,
    diff_summary: null,
    ab_test_summary: null,
    aggregate_metrics: null,
    workflow_config: null,
    baseline_scores: null,
    post_change_scores: null,
    review_artifacts: null,
    started_at: null,
    completed_at: null,
    error: null,
  };
}

/**
 * Reconstruct the full updated prompt by applying the current set of
 * proposed changes to the original prompt text.  Each change replaces
 * its `original_text` with `new_text`.
 */
function reconstructUpdatedPrompt(
  originalPrompt: string,
  proposedChanges: PromptChange[],
): string {
  let result = originalPrompt;
  for (const change of proposedChanges) {
    if (change.original_text && change.new_text != null) {
      result = result.replace(change.original_text, change.new_text);
    }
  }
  return result;
}

export function useCreateVersion(baseVersion?: number) {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id ?? '';

  const [state, setState] = useState<CreateVersionState>({
    phase: 'approach',
    intent: null,
    messages: [],
    plannerState: null,
    draftPlan: null,
    batchId: null,
    workflowStatus: null,
    reviewArtifacts: null,
    abTestSummary: null,
    newVersionNumber: null,
    error: null,
    isSending: false,
    rubricNames: [],
    evalRubrics: [],
    evalSetName: null,
    insightsLoading: false,
    suggestionLabels: [],
    pendingSimulationRequest: null,
  });

  const [activeWorkflow, setActiveWorkflow] = useState<ActiveWorkflowInfo | null>(null);

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const directSessionInFlightRef = useRef(false);
  const pollSessionRef = useRef(0);
  const pollRequestInFlightRef = useRef(false);
  const pollAbortControllerRef = useRef<AbortController | null>(null);
  const activeBatchIdRef = useRef<string | null>(null);
  const resumeCheckedRef = useRef(false);
  /** True once we've transitioned out of the `running` phase into a
   *  post-running phase (review / scores_ready / ab_results / done / error).
   *  Subsequent polls then only refresh `workflowStatus` (so the KG status
   *  card can update) and avoid re-overwriting `reviewArtifacts` — the user
   *  may have edited them inline via the changes sidebar.
   *  Reset whenever a new polling session starts. */
  const terminalPhaseEnteredRef = useRef(false);

  // ── helpers ──────────────────────────────────────────────────────────

  const patch = useCallback(
    (partial: Partial<CreateVersionState>) =>
      setState((prev) => ({ ...prev, ...partial })),
    [],
  );

  // ── fetch rubric names (for option pills) ───────────────────────────

  const loadRubricNames = useCallback(async () => {
    if (!orgId) return;
    try {
      const res = await performanceService.getOverview(orgId, { range: '7d' });
      if (res.success && res.data) {
        const names = res.data.rubrics.map((r) => r.name);
        patch({ rubricNames: names });
      }
    } catch {
      // Non-critical — pills will just be empty
    }
  }, [orgId, patch]);

  // ── fetch eval rubrics for insights card (score_driven) ───────────

  const loadEvalInsights = useCallback(async (): Promise<RubricOverviewItem[]> => {
    if (!orgId) return [];
    patch({ insightsLoading: true });
    try {
      // Try custom eval scores from simulation runs first
      const res = await performanceService.getOverview(orgId, {
        range: '7d',
        source: 'custom',
        data_source: 'eval',
      });

      if (res.success && res.data && res.data.rubrics.length > 0) {
        const rubrics = res.data.rubrics;
        patch({
          evalRubrics: rubrics,
          evalSetName: res.data.eval_set_name ?? null,
          rubricNames: rubrics.map((r) => r.name),
          insightsLoading: false,
        });
        return rubrics;
      }

      // Fallback: baseline eval scores from Langfuse evaluators
      const fallback = await performanceService.getOverview(orgId, {
        range: '7d',
        source: 'baseline',
        data_source: 'eval',
      });

      if (fallback.success && fallback.data && fallback.data.rubrics.length > 0) {
        const rubrics = fallback.data.rubrics;
        patch({
          evalRubrics: rubrics,
          evalSetName: fallback.data.eval_set_name ?? null,
          rubricNames: rubrics.map((r) => r.name),
          insightsLoading: false,
        });
        return rubrics;
      }

      // No eval data available — clear loading, pills will be empty
      patch({ insightsLoading: false });
      return [];
    } catch {
      // Non-critical — card won't render, fall back to normal planner flow
      patch({ insightsLoading: false });
      return [];
    }
  }, [orgId, patch]);

  // ── internal: call the planner and handle response ──────────────────

  const callPlanner = useCallback(
    async (
      allMessages: PlannerMessage[],
      plannerState: Record<string, unknown> | null,
      intent: 'score_driven' | 'targeted_revision',
      evalScores?: { name: string; score: number }[],
      attachments: File[] = [],
    ) => {
      const body: DraftRequest = {
        messages: allMessages,
        state: plannerState,
        optimization_intent: intent,
        pause_before_apply: true,
        enable_ab_test: true,
        auto_discover_datasets: true,
        ...(evalScores && evalScores.length > 0 ? { eval_scores: evalScores } : {}),
        ...(baseVersion != null ? { base_version: baseVersion } : {}),
      };

      const res = await versionWorkflowService.draft(orgId, body, attachments);
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Failed to get planner response');
      }
      return res.data;
    },
    [orgId, baseVersion],
  );

  const invalidatePollingSession = useCallback(() => {
    pollSessionRef.current += 1;
    pollRequestInFlightRef.current = false;
    if (pollAbortControllerRef.current) {
      pollAbortControllerRef.current.abort();
      pollAbortControllerRef.current = null;
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    invalidatePollingSession();
  }, [invalidatePollingSession]);

  // ── poll workflow status ────────────────────────────────────────────

  const startPolling = useCallback(
    (batchId: string) => {
      stopPolling();
      activeBatchIdRef.current = batchId;
      terminalPhaseEnteredRef.current = false;
      const sessionToken = pollSessionRef.current + 1;
      pollSessionRef.current = sessionToken;

      const isCurrentPollSession = () =>
        pollSessionRef.current === sessionToken &&
        activeBatchIdRef.current === batchId;

      const poll = async () => {
        if (!orgId || !isCurrentPollSession() || pollRequestInFlightRef.current) {
          return;
        }

        pollRequestInFlightRef.current = true;
        const controller = new AbortController();
        pollAbortControllerRef.current = controller;
        try {
          const res = await versionWorkflowService.getWorkflowStatus(
            orgId,
            batchId,
            controller.signal,
          );
          if (
            controller.signal.aborted ||
            !isCurrentPollSession() ||
            !res.success ||
            !res.data
          ) {
            return;
          }

          const ws = res.data;
          patch({ workflowStatus: ws });

          // Once we've transitioned to a post-running phase, subsequent polls
          // only exist to surface the parallel knowledge-gap analysis status
          // (which may finish after `awaiting_review`). Skip the terminal-state
          // branches below — they would otherwise overwrite locally-edited
          // reviewArtifacts. Stop polling once KG is no longer in flight.
          if (terminalPhaseEnteredRef.current) {
            if (!isKgInFlight(ws.knowledge_gap_status)) {
              stopPolling();
            }
            return;
          }

          // KG analysis is best-effort and must not keep the review screen
          // hanging on a workflow that already completed successfully. We
          // keep polling alive only when KG is still in flight (queued or
          // running) — see isKgInFlight at module scope.
          const kgInFlight = isKgInFlight(ws.knowledge_gap_status);

          if (ws.status === 'awaiting_review' && ws.review_artifacts) {
            terminalPhaseEnteredRef.current = true;
            patch({ reviewArtifacts: ws.review_artifacts, phase: 'review' });
            if (!kgInFlight) {
              stopPolling();
            }
          } else if (ws.status === 'scores_ready') {
            terminalPhaseEnteredRef.current = true;
            patch({
              workflowStatus: ws,
              reviewArtifacts: ws.review_artifacts ?? null,
              phase: 'scores_ready',
            });
            if (!kgInFlight) {
              stopPolling();
            }
          } else if (ws.status === 'cancelled') {
            terminalPhaseEnteredRef.current = true;
            stopPolling();
            patch({ error: 'Workflow was cancelled', phase: 'error' });
          } else if (ws.status === 'failed') {
            terminalPhaseEnteredRef.current = true;
            stopPolling();
            patch({ error: ws.error || 'Workflow failed', phase: 'error' });
          } else if (ws.status === 'workflow_complete_no_changes') {
            terminalPhaseEnteredRef.current = true;
            stopPolling();
            patch({
              error: 'The optimization found no changes to propose. Try providing more specific feedback or adjusting your focus areas.',
              phase: 'error',
            });
          } else if (
            ws.status.startsWith('workflow_complete') ||
            ws.status === 'completed'
          ) {
            terminalPhaseEnteredRef.current = true;
            if (!ws.new_prompt_version && !ws.review_artifacts) {
              stopPolling();
              patch({
                error:
                  'No changes could be proposed within the targeted scope. Try broadening your focus or targeting different areas.',
                phase: 'error',
              });
            } else if (ws.ab_test_summary) {
              patch({ abTestSummary: ws.ab_test_summary, phase: 'ab_results' });
              if (!kgInFlight) {
                stopPolling();
              }
            } else {
              const versionNum = ws.new_prompt_version
                ? parseInt(ws.new_prompt_version, 10) || null
                : null;
              patch({ newVersionNumber: versionNum, phase: 'done' });
              if (!kgInFlight) {
                stopPolling();
              }
            }
          }
        } catch {
          // Silent — will retry on next interval
        } finally {
          if (pollAbortControllerRef.current === controller) {
            pollAbortControllerRef.current = null;
          }
          if (isCurrentPollSession()) {
            pollRequestInFlightRef.current = false;
          }
        }
      };

      poll();
      pollingRef.current = setInterval(poll, 5000);
    },
    [orgId, patch, stopPolling],
  );

  // ── detect abandoned workflows on mount ──────────────────────────────

  useEffect(() => {
    if (!orgId || resumeCheckedRef.current) return;
    resumeCheckedRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        const res = await versionWorkflowService.listWorkflows(orgId, 5);
        if (cancelled || !res.success || !res.data) return;

        const active = res.data.find((w) => !TERMINAL_STATUSES.has(w.status));
        if (active) {
          setActiveWorkflow({
            batchId: active.batch_id,
            status: active.status,
            startedAt: active.started_at,
          });
        }
      } catch {
        // Non-critical — if the check fails, just proceed normally
      }
    })();

    return () => { cancelled = true; };
  }, [orgId]);

  // ── resume an abandoned workflow ─────────────────────────────────────

  const resumeWorkflow = useCallback(
    async (batchId: string) => {
      if (!orgId) return;
      setActiveWorkflow(null);

      try {
        const res = await versionWorkflowService.getWorkflowStatus(orgId, batchId);
        if (!res.success || !res.data) {
          throw new Error(res.error || 'Failed to fetch workflow status');
        }

        const ws = res.data;
        const intent: 'score_driven' | 'targeted_revision' | null =
          (ws.workflow_config?.optimization_intent as 'score_driven' | 'targeted_revision') ?? null;

        if (ws.status === 'awaiting_review' && ws.review_artifacts) {
          patch({
            batchId,
            workflowStatus: ws,
            reviewArtifacts: ws.review_artifacts,
            phase: 'review',
            intent,
          });
          // Resume mid-flight knowledge-gap analysis so the status card on
          // the review screen can update without a page reload. Order
          // matters: startPolling resets terminalPhaseEnteredRef to false
          // synchronously and kicks off poll() (async — first await
          // suspends before reading the ref), so setting the ref to true
          // immediately after is safe and ensures the early-exit guard
          // fires on the first poll (no clobber of just-patched state).
          if (isKgInFlight(ws.knowledge_gap_status)) {
            startPolling(batchId);
            terminalPhaseEnteredRef.current = true;
          }
        } else if (ws.status === 'scores_ready') {
          patch({
            batchId,
            workflowStatus: ws,
            reviewArtifacts: ws.review_artifacts ?? null,
            phase: 'scores_ready',
            intent,
          });
          if (isKgInFlight(ws.knowledge_gap_status)) {
            startPolling(batchId);
            terminalPhaseEnteredRef.current = true;
          }
        } else if (TERMINAL_STATUSES.has(ws.status)) {
          // Workflow finished between detection and resume — just clear
          patch({ phase: 'approach' });
        } else {
          // Still running — show progress and start polling
          patch({
            batchId,
            workflowStatus: ws,
            phase: 'running',
            intent,
          });
          startPolling(batchId);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to resume workflow';
        patch({ error: message, phase: 'error' });
      }
    },
    [orgId, patch, startPolling],
  );

  // ── reset to start over ────────────────────────────────────────────

  const reset = useCallback(() => {
    stopPolling();
    directSessionInFlightRef.current = false;
    activeBatchIdRef.current = null;
    setState({
      phase: 'approach',
      intent: null,
      messages: [],
      plannerState: null,
      draftPlan: null,
      batchId: null,
      workflowStatus: null,
      reviewArtifacts: null,
      abTestSummary: null,
      newVersionNumber: null,
      error: null,
      isSending: false,
      rubricNames: [],
      evalRubrics: [],
      evalSetName: null,
      insightsLoading: false,
      suggestionLabels: [],
      pendingSimulationRequest: null,
    });
  }, [stopPolling]);

  // ── cancel abandoned workflow and start fresh ────────────────────────

  const cancelAndStartFresh = useCallback(
    async (batchId: string) => {
      if (!orgId) return;
      setActiveWorkflow(null);

      try {
        await versionWorkflowService.cancelWorkflow(orgId, batchId);
      } catch {
        // Best-effort — even if cancel fails, let the user proceed
      }
      // Reset to initial state so the user can start a new workflow
      reset();
    },
    [orgId, reset],
  );

  // ── start the optimization workflow ─────────────────────────────────

  useEffect(() => () => stopPolling(), [stopPolling]);

  const startWorkflow = useCallback(
    async (workflowRequest: Record<string, unknown>) => {
      if (!orgId) return;
      patch({ phase: 'running' });

      try {
        const res = await versionWorkflowService.startWorkflow(orgId, workflowRequest);
        if (!res.success || !res.data) {
          throw new Error(res.error || 'Failed to start workflow');
        }

        const batchId = res.data.batch_id;
        patch({
          batchId,
          workflowStatus: buildWorkflowStatusSnapshot(orgId, batchId, res.data.status),
        });
        startPolling(batchId);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to start workflow';
        patch({ error: message, phase: 'error' });
      }
    },
    [orgId, patch, startPolling],
  );

  const generateChanges = useCallback(
    async (workflowRequest: Record<string, unknown>) => {
      if (!orgId) return;
      patch({ phase: 'running' });

      try {
        const res = await versionWorkflowService.generateChanges(orgId, workflowRequest);
        if (!res.success || !res.data) {
          throw new Error(res.error || 'Failed to generate prompt changes');
        }

        const batchId = res.data.batch_id;
        patch({
          batchId,
          workflowStatus: buildWorkflowStatusSnapshot(orgId, batchId, res.data.status),
        });
        startPolling(batchId);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to generate prompt changes';
        patch({ error: message, phase: 'error' });
      }
    },
    [orgId, patch, startPolling],
  );

  const startDirectSession = useCallback(() => {
    if (!orgId || state.isSending || directSessionInFlightRef.current) return;

    // Latch the in-flight ref while sitting on the confirm card so a second
    // click on "Simulate & Score" (or another approach button) is ignored
    // until the user picks a run mode or resets.
    directSessionInFlightRef.current = true;

    const workflowRequest: Record<string, unknown> = {
      mode: 'batch_simulation',
      optimization_intent: 'score_driven',
      auto_discover_datasets: true,
      score_threshold: 60,
      max_changes: 5,
      pause_before_apply: true,
      enable_ab_test: true,
      run_custom_evaluators: true,
      ...(baseVersion != null ? { base_version: baseVersion } : {}),
    };

    setState((prev) => ({
      ...prev,
      intent: 'score_driven',
      phase: 'confirm_simulation',
      error: null,
      suggestionLabels: [],
      pendingSimulationRequest: workflowRequest,
    }));
  }, [orgId, state.isSending, baseVersion]);

  const confirmSimulationRun = useCallback(
    async (runKnowledgeGap: boolean) => {
      const pending = state.pendingSimulationRequest;
      // Consume the in-flight latch armed by `startDirectSession` synchronously
      // so a rapid double-click on the confirm card can't fire `startWorkflow`
      // twice within the same render frame (before `isSending` flips).
      if (!orgId || !pending || !directSessionInFlightRef.current) return;
      directSessionInFlightRef.current = false;

      const workflowRequest = {
        ...pending,
        run_knowledge_gap_analysis: runKnowledgeGap,
      };

      setState((prev) => ({
        ...prev,
        isSending: true,
        pendingSimulationRequest: null,
        messages: [
          ...prev.messages,
          {
            id: nextMsgId(),
            role: 'assistant' as const,
            content:
              "Running simulated buyer conversations now. I’ll score the results and suggest changes if needed.",
            ts: Date.now(),
          },
        ],
      }));

      try {
        await startWorkflow(workflowRequest);
      } finally {
        patch({ isSending: false });
      }
    },
    [orgId, patch, startWorkflow, state.pendingSimulationRequest],
  );

  const confirmDraftPlan = useCallback(
    async (opts?: { runKnowledgeGap?: boolean }) => {
      if (!state.draftPlan) return;
      const workflowRequest = {
        ...state.draftPlan.workflow_request,
        run_knowledge_gap_analysis: opts?.runKnowledgeGap ?? false,
      };
      if (state.intent === 'score_driven') {
        await startWorkflow(workflowRequest);
      } else {
        await generateChanges(workflowRequest);
      }
    },
    [generateChanges, startWorkflow, state.draftPlan, state.intent],
  );

  const handlePlannerResponse = useCallback(
    (
      data: DraftResponse,
      intent: 'score_driven' | 'targeted_revision',
    ) => {
      setState((prev) => ({
        ...prev,
        isSending: false,
        plannerState: data.state,
        suggestionLabels: data.suggestion_labels ?? [],
        messages: [
          ...prev.messages,
          { id: nextMsgId(), role: 'assistant' as const, content: data.assistant_message, ts: Date.now() },
        ],
        ...(data.status === 'ready' && data.draft_plan
          ? { draftPlan: data.draft_plan, phase: 'confirm' as Phase }
          : {}),
      }));
    },
    [],
  );

  // ── approach selection ──────────────────────────────────────────────

  const selectApproach = useCallback(
    (intent: 'score_driven' | 'targeted_revision') => {
      const label =
        intent === 'score_driven' ? 'Review Insights' : 'Share Your Feedback';
      const plannerMsg =
        intent === 'score_driven'
          ? 'Based on the eval scores provided, start by identifying the weakest scoring areas and briefly explain what gaps you see. Then ask 1-2 specific questions about how the customer currently handles those areas in practice — for example, how they handle objections, what their ideal flow looks like, or what context the agent is missing. These questions should help you understand the real-world process so you can patch the gaps. Do not open with filler like "Happy to help" — start directly with your analysis.'
          : undefined;

      setState((prev) => ({
        ...prev,
        intent,
        phase: 'chat',
        isSending: true,
        messages: [
          ...prev.messages,
          { id: nextMsgId(), role: 'user' as const, content: label, ...(plannerMsg ? { plannerContent: plannerMsg } : {}), ts: Date.now() },
        ],
      }));

      if (intent === 'score_driven') {
        // Fetch eval insights, then call planner with scores to get
        // an assistant message + LLM-generated suggestion labels.
        (async () => {
          try {
            const rubrics = await loadEvalInsights();
            const evalScores = rubrics
              .filter((r) => r.score_7d != null)
              .map((r) => ({ name: r.name, score: r.score_7d as number }));

            const data = await callPlanner(
              [{ role: 'user', content: plannerMsg! }],
              null,
              intent,
              evalScores,
            );

            setState((prev) => ({
              ...prev,
              isSending: false,
              plannerState: data.state,
              suggestionLabels: data.suggestion_labels ?? [],
              messages: [
                ...prev.messages,
                { id: nextMsgId(), role: 'assistant' as const, content: data.assistant_message, ts: Date.now() },
              ],
              ...(data.status === 'ready' && data.draft_plan
                ? { draftPlan: data.draft_plan, phase: 'confirm' as Phase }
                : {}),
            }));
          } catch (err) {
            const message = err instanceof Error ? err.message : 'Unknown error';
            patch({ isSending: false, error: message, phase: 'error' });
          }
        })();
      } else {
        // targeted_revision — call planner immediately as before
        loadRubricNames();
        (async () => {
          try {
            const data = await callPlanner(
              [{ role: 'user', content: label }],
              null,
              intent,
            );
            handlePlannerResponse(data, intent);
          } catch (err) {
            const message = err instanceof Error ? err.message : 'Unknown error';
            patch({ isSending: false, error: message, phase: 'error' });
          }
        })();
      }
    },
    [callPlanner, handlePlannerResponse, loadRubricNames, loadEvalInsights, patch],
  );

  // ── send a chat message to the planner ──────────────────────────────

  const sendMessage = useCallback(
    async (content: string, attachments: File[] = []): Promise<boolean> => {
      if (!orgId || state.isSending) return false;

      const isFromApproach = state.phase === 'approach';
      const intent = state.intent || 'targeted_revision';

      setState((prev) => ({
        ...prev,
        phase: 'chat',
        isSending: true,
        intent,
        messages: [
          ...prev.messages,
          { id: nextMsgId(), role: 'user' as const, content: content || 'Attached temporary context for this turn.', ts: Date.now() },
        ],
      }));

      if (isFromApproach) {
        loadRubricNames();
      }

      const allMessages: PlannerMessage[] = [
        ...state.messages.map((m) => ({ role: m.role, content: m.plannerContent ?? m.content })),
        { role: 'user' as const, content: content || 'Attached temporary context for this turn.' },
      ];

      try {
        const data = await callPlanner(allMessages, state.plannerState, intent, undefined, attachments);
        handlePlannerResponse(data, intent);
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        patch({ isSending: false, error: message, phase: 'error' });
        return false;
      }
    },
    [orgId, state.isSending, state.messages, state.plannerState, state.intent, state.phase, callPlanner, handlePlannerResponse, patch, loadRubricNames],
  );

  // ── apply reviewed prompt (save as staging) ─────────────────────────

  const applyPrompt = useCallback(
    async (
      updatedPrompt?: string,
      action: ApplyAction | boolean = 'save',
    ) => {
      if (!orgId || !state.batchId) return;
      const isLegacyMode = typeof action === 'boolean';
      const explicitAction = isLegacyMode ? null : action;
      const enableAbTest = isLegacyMode ? action : action === 'run_ab_test';
      const optimisticStatus =
        explicitAction === 'run_scores' && state.intent === 'targeted_revision'
          ? buildWorkflowStatusSnapshot(orgId, state.batchId, 'running_preview_experiments')
          : null;

      patch(
        optimisticStatus
          ? {
              phase:
                enableAbTest
                  ? 'ab_running'
                  : explicitAction === 'run_scores'
                    ? 'running'
                    : 'saving',
              workflowStatus: optimisticStatus,
            }
          : {
              phase:
                enableAbTest
                  ? 'ab_running'
                  : explicitAction === 'run_scores'
                    ? 'running'
                    : 'saving',
            },
      );

      try {
        const res = await versionWorkflowService.applyReviewedPrompt(orgId, state.batchId, {
          updated_prompt: updatedPrompt ?? null,
          action: explicitAction,
          enable_ab_test: isLegacyMode
            ? enableAbTest
            : explicitAction === 'run_ab_test'
              ? true
              : explicitAction === 'save'
                ? false
                : null,
        });

        if (!res.success || !res.data) {
          throw new Error(res.error || 'Failed to apply prompt');
        }

        patch({
          workflowStatus: buildWorkflowStatusSnapshot(orgId, state.batchId, res.data.status),
        });
        startPolling(state.batchId);
      } catch (err) {
        const raw = err instanceof Error ? err.message : 'Failed to save';
        // Browser fetch errors ("Load failed" on Safari, "Failed to fetch" on
        // Chrome) are opaque — surface a friendlier message so the user knows
        // to retry rather than seeing a cryptic browser-internal string.
        const isNetworkError =
          /load failed|failed to fetch|networkerror|network request failed/i.test(raw);
        const message = isNetworkError
          ? 'A network error occurred — please check your connection and try again.'
          : raw;
        patch({ error: message, phase: 'error' });
      }
    },
    [orgId, state.batchId, state.intent, patch, startPolling],
  );

  // ── edit/delete proposed changes in review ─────────────────────────

  const removeChange = useCallback((changeId: string) => {
    setState((prev) => {
      if (!prev.reviewArtifacts) return prev;
      const updatedChanges = prev.reviewArtifacts.proposed_changes.filter(
        (c) => c.change_id !== changeId,
      );
      const updatedPrompt = prev.reviewArtifacts.original_prompt
        ? reconstructUpdatedPrompt(prev.reviewArtifacts.original_prompt, updatedChanges)
        : prev.reviewArtifacts.updated_prompt;
      return {
        ...prev,
        reviewArtifacts: {
          ...prev.reviewArtifacts,
          proposed_changes: updatedChanges,
          updated_prompt: updatedPrompt,
        },
      };
    });
  }, []);

  const updateChangeText = useCallback((changeId: string, newText: string) => {
    setState((prev) => {
      if (!prev.reviewArtifacts) return prev;
      const updatedChanges = prev.reviewArtifacts.proposed_changes.map((c) =>
        c.change_id === changeId ? { ...c, new_text: newText } : c,
      );
      const updatedPrompt = prev.reviewArtifacts.original_prompt
        ? reconstructUpdatedPrompt(prev.reviewArtifacts.original_prompt, updatedChanges)
        : prev.reviewArtifacts.updated_prompt;
      return {
        ...prev,
        reviewArtifacts: {
          ...prev.reviewArtifacts,
          proposed_changes: updatedChanges,
          updated_prompt: updatedPrompt,
        },
      };
    });
  }, []);

  // ── cancel a running workflow ───────────────────────────────────────

  const cancelWorkflow = useCallback(async () => {
    if (!orgId || !state.batchId) return;
    stopPolling();
    try {
      await versionWorkflowService.cancelWorkflow(orgId, state.batchId);
    } catch {
      // Best-effort — even if the API call fails, transition to error
    }
    patch({ error: 'Workflow was cancelled', phase: 'error' });
  }, [orgId, state.batchId, stopPolling, patch]);

  // ── refine from review — go back to chat with context ──────────────

  const requestRevisions = useCallback(() => {
    const artifacts = state.reviewArtifacts;
    const changeCount = artifacts?.proposed_changes?.length ?? 0;
    const sectionNames = artifacts?.proposed_changes
      ?.map((c) => c.section_name)
      .filter((name, i, arr) => arr.indexOf(name) === i) ?? [];

    // Build a specific, conversational re-entry message
    let refineSummary: string;
    if (artifacts?.conversational_summary) {
      refineSummary = artifacts.conversational_summary;
    } else if (artifacts?.changes_summary) {
      refineSummary = artifacts.changes_summary;
    } else if (artifacts?.feedback_summary) {
      refineSummary = artifacts.feedback_summary;
    } else {
      refineSummary = 'changes were proposed';
    }

    const sectionList = sectionNames.length > 0
      ? `\n\nSections modified: **${sectionNames.join('**, **')}**`
      : '';

    const content =
      `Here's what I changed: ${refineSummary}${sectionList}\n\n` +
      `${changeCount > 0 ? `I made ${changeCount} change${changeCount !== 1 ? 's' : ''}. ` : ''}` +
      'What would you like me to adjust? For example, you can ask me to:\n' +
      '- Revise a specific section\n' +
      '- Make the tone more aggressive or softer\n' +
      '- Add or remove specific behaviors';

    setState((prev) => ({
      ...prev,
      phase: 'chat',
      draftPlan: null,
      batchId: null,
      workflowStatus: null,
      reviewArtifacts: null,
      abTestSummary: null,
      messages: [
        ...prev.messages,
        {
          id: nextMsgId(),
          role: 'assistant' as const,
          content,
          ts: Date.now(),
        },
      ],
    }));
  }, [state.reviewArtifacts]);

  return {
    state,
    activeWorkflow,
    selectApproach,
    startDirectSession,
    confirmSimulationRun,
    confirmDraftPlan,
    sendMessage,
    applyPrompt,
    removeChange,
    updateChangeText,
    cancelWorkflow,
    resumeWorkflow,
    cancelAndStartFresh,
    requestRevisions,
    reset,
    stopPolling,
  };
}
